'use client';

import { getFeaturedProductCard } from '@lib/data/products';
import ProductPreview from '@modules/products/components/product-preview';
import SkeletonProductPreview from '@modules/skeletons/components/skeleton-product-preview';
import { useEffect, useState } from 'react';
import type { ProductCardVM } from 'types/view-models';

/**
 * Client-side counterpart to `FeaturedProduct` (`./index.tsx`, still used
 * as-is elsewhere) for use on statically-generated legacy content pages.
 * Fetches via a Server Action after mount instead of during render, so the
 * `_pricing_channel` cookie read in `listProducts` doesn't force the page
 * dynamic and conflict with `generateStaticParams`.
 */
export default function FeaturedProductClient({ handle }: { handle: string }) {
  const [card, setCard] = useState<ProductCardVM | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    getFeaturedProductCard(handle)
      .then((result) => {
        if (!cancelled) {
          setCard(result);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [handle]);

  if (isLoading) {
    return (
      <div
        className="mt-8 max-w-[280px]"
        data-testid="featured-product-skeleton"
      >
        <SkeletonProductPreview />
      </div>
    );
  }

  if (!card) {
    return null;
  }

  return (
    <div className="mt-8 max-w-[280px]">
      <ProductPreview card={card} />
    </div>
  );
}
